import React from "react";
import { Box, IconButton, Modal, Typography, styled } from "@mui/material";
import { RichText } from "./RichText";

// Inline close icon SVG (eva:close-fill equivalent)
const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fill="currentColor"
      d="m13.41 12l4.3-4.29a1 1 0 1 0-1.42-1.42L12 10.59l-4.29-4.3a1 1 0 0 0-1.42 1.42l4.3 4.29l-4.3 4.29a1 1 0 0 0 0 1.42a1 1 0 0 0 1.42 0l4.29-4.3l4.29 4.3a1 1 0 0 0 1.42 0a1 1 0 0 0 0-1.42Z"
    />
  </svg>
);

// Yellow "label-important" chevron bullet (material-symbols), inlined as an SVG
// data URL so list items in facet descriptions get the branded chevron marker.
const bulletDataUrl =
  "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='24'%20height='24'%20viewBox='0%200%2024%2024'%3E%3Cpath%20fill='%23ffab00'%20d='M15%2020H4q-.625%200-.9-.55t.1-1.05L8%2012L3.2%205.6q-.375-.5-.1-1.05T4%204h11q.475%200%20.9.212t.7.588l4.5%206q.4.525.4%201.2t-.4%201.2l-4.5%206q-.275.375-.7.588T15%2020'/%3E%3C/svg%3E";

export interface FacetDefinition {
  facetCode: string;
  longLabel: string;
  icon?: string;
  color?: string;
  secondaryColor?: string;
  description?: unknown;
  variable?: string;
  facet?: string;
  iconifyIcon?: string;
}

export interface FacetInfoModalProps {
  open: boolean;
  onClose: () => void;
  facet: FacetDefinition | null;
  renderFallbackIcon?: (
    facet: FacetDefinition,
    size: number,
  ) => React.ReactNode;
}

export const FacetInfoModal = ({
  open,
  onClose,
  facet,
  renderFallbackIcon,
}: FacetInfoModalProps) => {
  if (!facet) return null;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="facet-info-modal">
      <ModalContent>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>

        <ModalHeader>
          {facet.icon ? (
            <FacetIcon src={facet.icon} alt={facet.variable} />
          ) : renderFallbackIcon ? (
            renderFallbackIcon(facet, 64)
          ) : null}
          <Typography variant="h4" component="h2">
            {facet.longLabel}
          </Typography>
        </ModalHeader>

        <ModalBody>
          <RichText value={facet.description as string | undefined} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled(Box)({
  label: "FacetInfoModalContent",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "white",
  borderRadius: "16px",
  padding: "32px",
  width: "90%",
  border: "none",
  outline: "none",
  maxWidth: "480px",
  maxHeight: "90vh",
  overflowY: "auto",
  "& ul": {
    paddingLeft: 0,
    paddingRight: "20px",
    listStyle: "none",
    "& li": {
      backgroundImage: `url("${bulletDataUrl}")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "-4px 5px",
      backgroundSize: "18px",
      paddingLeft: "24px",
    },
  },
});

const CloseButton = styled(IconButton)({
  label: "FacetInfoCloseButton",
  position: "absolute",
  top: "16px",
  right: "16px",
});

const ModalHeader = styled(Box)({
  label: "FacetInfoModalHeader",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: "16px",
  marginBottom: "24px",
});

const ModalBody = styled(Box)({ label: "FacetInfoModalBody" });

const FacetIcon = styled("img")({
  label: "FacetInfoIcon",
  width: "64px",
  height: "64px",
});
